import { useRef } from 'react'

import { useEventListener, useMemoizedFn } from 'ahooks'

import { findParentElement, isHTMLElement } from '../../../utils/dom'

import { UseTableIntractionProps } from './interface'

const isCellElement = (target: HTMLElement): target is HTMLTableCellElement => {
  const caseInsensitiveTagName = target.tagName.toUpperCase()
  return caseInsensitiveTagName === 'TD' || caseInsensitiveTagName === 'TH'
}

const isTableElement = (target: HTMLElement): target is HTMLTableElement => {
  const caseInsensitiveTagName = target.tagName.toUpperCase()
  return caseInsensitiveTagName === 'TABLE'
}

/**
 * 计算 cell 在 table 中的行跟列下标
 *
 * @param tableElement table 元素
 * @param cellElement cell 元素
 */
const calcCellGrid = (
  tableElement: HTMLTableElement,
  cellElement: HTMLTableCellElement
) => {
  // 遍历 table 所有行跟列，需要注意合并单元格的情况
  const rows = Array.from(tableElement.rows)
  const rowIndex = rows.findIndex(row => row.contains(cellElement))
  const row = rows[rowIndex]
  const cells = Array.from(row.cells)
  const cellIndex = cells.findIndex(cell => cell === cellElement)
  const colIndex = cells
    .slice(0, cellIndex + 1)
    .reduce(
      (acc, cell) => acc + Number.parseInt(cell.getAttribute('colspan') || '1'),
      -1
    )
  return {
    rowIndex,
    colIndex
  }
}

/**
 * 处理 table 交互的 hook
 */
export const useTableInteraction = ({
  rootElement,
  isMatchedTarget,
  subscribe
}: UseTableIntractionProps) => {
  const rootElementRef = useRef<HTMLElement>(null)
  // 防止跟选中操作冲突
  const mouseDownRef = useRef(false)
  const eventTargetRef = useRef<HTMLElement>(null)
  const activeCellElementRef = useRef<HTMLElement>(null)
  const memoizedCallback = useMemoizedFn(subscribe)

  const resetState = () => {
    eventTargetRef.current = null
    activeCellElementRef.current = null
    memoizedCallback({
      tableElement: null,
      cellElement: null,
      rowIndex: -1,
      colIndex: -1
    })
  }

  const handleMouseDown = () => {
    mouseDownRef.current = true
  }

  const handleMouseMove = (event: MouseEvent) => {
    const target = event.target
    if (mouseDownRef.current || !isHTMLElement(target)) {
      resetState()
      return
    }
    if (eventTargetRef.current !== target) {
      eventTargetRef.current = target
      const cellElement = isCellElement(target)
        ? target
        : findParentElement({
            target,
            root: rootElementRef.current,
            $predicate: isCellElement
          })
      if (!cellElement || isMatchedTarget?.(cellElement) === false) {
        resetState()
        return
      }
      if (cellElement && activeCellElementRef.current !== cellElement) {
        const tableElement = findParentElement({
          target,
          root: rootElementRef.current,
          $predicate: isTableElement
        })
        if (!tableElement) {
          throw new Error('TableCellResizer: Table element not found.')
        }
        activeCellElementRef.current = cellElement
        memoizedCallback({
          tableElement,
          cellElement,
          ...calcCellGrid(tableElement, cellElement)
        })
      }
    }
  }

  const handleMouseUp = () => {
    mouseDownRef.current = false
  }

  useEventListener('mousedown', handleMouseDown, {
    target: rootElement,
    passive: true
  })

  useEventListener('mousemove', handleMouseMove, {
    target: rootElement,
    passive: true
  })

  useEventListener('mouseup', handleMouseUp, {
    target: rootElement,
    passive: true
  })
}
